import React, { useState } from 'react';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import dateFnsLocaleEn from 'date-fns/locale/en-US';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { format } from 'date-fns';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStyles } from './styles';

type DatePickerProps = {
    label?: string;
    value: Date | null;
    onChange: (
      value: any,
      context: PickerChangeHandlerContext<DateValidationError>,
    ) => void;
    minDate?: Date;
    maxDate?: Date;
    name?: string;
    error?: string;
  };
  
export const DatePicker = ({
    label,
    value,
    onChange,
    minDate,
    maxDate,
    name,
    error:customError
  }: DatePickerProps) => {
    const [error, setError] = useState(customError || '');
    const theme = useTheme();
    const styles = getStyles(theme);

    const getErrorMessage = (error: string) => {
      if (error === 'invalidDate') {
        return `Invalid date format`;
      }
  
      if (error === 'minDate') {
        return `Date can't be earlier than ${format(minDate!, 'MM/dd/yyyy')}`;
      }
  
      if (error === 'maxDate') {
        return `Date can't be later than ${format(maxDate!, 'MM/dd/yyyy')}`;
      }
  
      return '';
    };
  
    return (
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={dateFnsLocaleEn}
        localeText={{
          cancelButtonLabel: 'Cancel',
          okButtonLabel: 'Ok',
        }}
      >
        <Box data-cy="keyboard-date-picker" data-testid="keyboard-date-picker" sx={{ width: '100%' }}>
        <MuiDatePicker
          slotProps={{
            textField: {
              name,
              sx: styles.datePickerLabel,
              slotProps: {
                input: {
                  sx: styles.datePickerInput,
                },
              },
              hiddenLabel: true,
              margin: 'none',
              size: 'small',
              error: !!error,
              helperText: error,
            },
            openPickerButton: {
              sx: styles.datePickerButton,
            },
            openPickerIcon: {
              sx: styles.datePickerIcon,
            },
            inputAdornment: {
              sx: styles.datePickerInputAdornment,
            },
            day: {
              sx: styles.datePickerDay,
            },
          }}
          sx={styles.datePicker}
          label={label}
          value={value}
          format='MM/dd/yyyy'
          onChange={onChange}
          minDate={minDate}
          maxDate={maxDate}
          orientation='portrait'
          reduceAnimations
          onError={(err) => {
            const errorMessage = getErrorMessage(err!);
            setError(errorMessage);
          }}
          data-userpilot='date-picker'
        />
        </Box>
      </LocalizationProvider>
    );
  };
  